/**
 * Phase 17 — live countdown for an admin offer's `expires_at`.
 *
 * Uses `formatCountdown` from `@starz/shared/offer`. Re-renders every
 * 60 seconds via `setInterval` — minute-resolution is the right
 * cadence for negotiation pressure without flicker.
 */

import { useEffect, useState } from "react";

import { formatCountdown, isExpired, remainingMs } from "@starz/shared";

interface Props {
  expiresAtIso: string;
  className?: string;
}

const TICK_MS = 60_000;

export function OfferCountdown({ expiresAtIso, className }: Props) {
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [expiresAtIso]);

  const ms = remainingMs(expiresAtIso);
  const expired = isExpired(expiresAtIso);
  const label = formatCountdown(ms);
  const color = expired
    ? "text-warm-gray"
    : ms < 60 * 60 * 1000
      ? "text-starz-brick"
      : "text-starz-navy";
  return (
    <span
      className={`text-xs font-medium ${color} ${className ?? ""}`}
      title={`Expires at ${new Date(expiresAtIso).toLocaleString()}`}
    >
      {expired ? "Offer expired" : `Offer expires in ${label}`}
    </span>
  );
}
